import { createRouter, createWebHistory, createWebHashHistory } from 'vue-router'
import Layout from '../views/layout/Index.vue'
import { canAccessPage } from '../utils/permission'
import { useCompanyAccessStore } from '../store/modules/companyAccess'
import { isElectron } from '../utils/electron'
const { SHOW_NAVIGATION } = require('../../config')

// 检测是否在 Electron 环境中
// 方法1: 检查 window.electronAPI（由 preload.js 暴露）
// 方法2: 检查 userAgent
// 方法3: 检查 window.location.protocol 是否为 file:
const isElectronEnv = typeof window !== 'undefined' && (
  window.electronAPI !== undefined ||
  window.navigator?.userAgent?.indexOf('Electron') !== -1 ||
  window.location?.protocol === 'file:'
)

// Electron 使用 Hash 路由。若落地页是 /login?token=... 或 /dashboard?token=...（pathname 无 hash），
// 必须在 createRouter 之前改成 /#/login?... 或 /#/dashboard?...，否则路由会按空 hash 匹配到 / 导致白屏
if (typeof window !== 'undefined' && isElectronEnv) {
  const pathname = window.location.pathname.replace(/\/$/, '') || '/'
  const search = window.location.search || ''
  const hash = window.location.hash || ''
  
  // 情况1：pathname 是 /login 或 /dashboard，有 search 但没有 hash（需要转换）
  if (search && !hash && (pathname === '/login' || pathname === '/dashboard')) {
    const target = window.location.origin + '/#' + pathname + search
    window.history.replaceState(null, '', target)
  }
  // 情况2：pathname 是 /login 或 /dashboard，同时有 hash（格式错误，需要修正）
  // 例如：http://127.0.0.1:8080/login#/login → http://127.0.0.1:8080/#/login
  else if (hash && (pathname === '/login' || pathname === '/dashboard')) {
    // 如果 hash 已经是正确的格式（#/login 或 #/dashboard），只保留 hash，清除 pathname
    if (hash.startsWith('#/')) {
      const target = window.location.origin + hash + (search || '')
      window.history.replaceState(null, '', target)
    }
  }
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/HomePage.vue'),
    meta: { title: '声聚互联', requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/login/Index.vue'),
    meta: { title: '登录', requiresAuth: false }
  },
  {
    path: '/navigation',
    name: 'Navigation',
    component: () => import('../views/Navigation.vue'),
    meta: { title: '系统导航', requiresAuth: true },
    children: [
      {
        path: '',
        name: 'NavigationHome',
        component: { render: () => null }, // 首页内容由Navigation.vue自身渲染
      },
      {
        path: 'audition',
        name: 'NavigationAudition',
        component: () => import('../views/audition/AuditionHall.vue'),
      },
      {
        path: 'publish',
        name: 'NavigationPublish',
        component: () => import('../views/audition/AuditionTaskEdit.vue'),
      },
      {
        path: 'review',
        name: 'NavigationReview',
        component: () => import('../views/audition/AuditionReview.vue'),
      },
    ]
  },
  {
    path: '/audition',
    name: 'AuditionHall',
    component: () => import('../views/audition/AuditionHall.vue'),
    meta: { title: '试音大厅', requiresAuth: true }
  },
  {
    path: '/audition/task-edit',
    name: 'AuditionTaskEdit',
    component: () => import('../views/audition/AuditionTaskEdit.vue'),
    meta: { title: '新建试音任务', requiresAuth: true }
  },
  {
    path: '/audition/review',
    name: 'AuditionReview',
    component: () => import('../views/audition/AuditionReview.vue'),
    meta: { title: '试音评审', requiresAuth: true }
  },
  {
    path: '/audition/review-list',
    name: 'ReviewList',
    component: () => import('@/views/audition/ReviewList.vue')
  },
  {
    path: '/dashboard',
    name: 'Layout',
    component: Layout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('../views/dashboard/Index.vue'),
        meta: { title: '首页', icon: 'HomeFilled', permission: 'page:dashboard' }
      },
      {
        path: '/audio',
        redirect: '/audio/list',
        meta: { title: '音频管理', icon: 'Headset', permission: 'page:audio', requiresAuth: true }
      },
      {
        path: '/audio/list',
        name: 'Audio',
        component: () => import('../views/audio/Index.vue'),
        meta: { title: '音频管理', icon: 'Headset', permission: 'page:audio', requiresAuth: true }
      },
      {
        path: '/audio/ai-voice-library',
        name: 'AiVoiceLibrary',
        component: () => import('../views/audio/AiVoiceLibrary.vue'),
        meta: { title: 'AI音库', icon: 'Microphone', permission: 'page:audio', requiresAuth: true }
      },
      {
        path: '/voice-clone',
        name: 'VoiceClone',
        component: () => import('../views/audio/VoiceClone.vue'),
        meta: { title: '高级声音生成', icon: 'Microphone', requiresAuth: true }
      },
      {
        path: '/ai-video-dub',
        name: 'AiVideoDub',
        component: () => import('../views/aigen/AiVideoDub.vue'),
        meta: { title: 'AI配音', icon: 'VideoCameraFilled', requiresAuth: true }
      },
      {
        path: '/ai-video-dub/guide',
        name: 'AiVideoDubGuide',
        component: () => import('../views/aigen/AiVideoDubGuide.vue'),
        meta: { title: 'AI配音使用教程', requiresAuth: true }
      },
      {
        path: '/audio/voice-generation',
        name: 'VoiceGeneration',
        component: () => import('../views/audio/VoiceGeneration.vue'),
        meta: { title: '声音生成', icon: 'Microphone', requiresAuth: true }
      },
      {
        path: '/audio/ai-audiobook',
        redirect: '/script/online'
      },
      {
        path: '/progress',
        name: 'ProgressStatistics',
        component: () => import('../views/team/ProgressStatistics.vue'),
        meta: { title: '作品进度管理', icon: 'DataAnalysis', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/voice/showcase',
        name: 'VoiceShowcase',
        component: () => import('../views/voice/VoiceShowcase.vue'),
        meta: { title: '个人声展', icon: 'Avatar', requiresAuth: true }
      },
      {
        path: '/voice/showcase-registry',
        name: 'VoiceShowcaseRegistry',
        component: () => import('../views/voice/VoiceShowcaseRegistry.vue'),
        meta: { title: '声展清单', icon: 'List', requiresAuth: true, adminOnly: true }
      },
      // 画本管理
      {
        path: '/script',
        redirect: '/script/chapters',
        meta: { title: '画本管理', requiresAuth: true }
      },
      {
        path: '/script/chapters',
        name: 'ScriptChapterManage',
        component: () => import('../views/script/ChapterManage.vue'),
        meta: { title: '章节管理', icon: 'Document', requiresAuth: true }
      },
      {
        path: '/script/online',
        name: 'ScriptOnline',
        component: () => import('../views/script/ScriptOnline.vue'),
        meta: { title: '在线画本', icon: 'EditPen', requiresAuth: true }
      },
      {
        path: '/script/my-works',
        name: 'ScriptMyWorks',
        component: () => import('../views/script/MyWorks.vue'),
        meta: { title: '我参与的作品', icon: 'User', requiresAuth: true }
      },
      {
        path: '/settlements',
        name: 'Settlements',
        component: () => import('../views/settlements/Index.vue'),
        meta: { title: '结算申请管理', icon: 'Document', permission: 'page:settlements', requiresAuth: true }
      },
      {
        path: '/audits',
        name: 'Audits',
        component: () => import('../views/audits/Index.vue'),
        meta: { title: '结算审核管理', icon: 'Check', permission: 'page:audits', requiresAuth: true }
      },
      {
        path: '/payments',
        name: 'Payments',
        component: () => import('../views/payments/Index.vue'),
        meta: { title: '结算支付管理', icon: 'Money', permission: 'page:payments', requiresAuth: true }
      },
      {
        path: '/statistics',
        name: 'Statistics',
        component: () => import('../views/statistics/Index.vue'),
        meta: { title: '结算统计分析', icon: 'DataAnalysis', permission: 'page:statistics', requiresAuth: true }
      },
      {
        path: '/recharge',
        name: 'Recharge',
        component: () => import('../views/recharge/RechargePage.vue'),
        meta: { title: '余额充值', requiresAuth: true }
      },
      {
        path: '/recharge/orders',
        name: 'RechargeOrders',
        component: () => import('../views/recharge/OrderList.vue'),
        meta: { title: '充值记录', requiresAuth: true }
      },
      {
        path: '/team',
        name: 'Team',
        component: () => import('../views/team/Index.vue'),
        meta: { title: '制作组管理', icon: 'User', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/team/storage',
        name: 'TeamStorage',
        component: () => import('../views/team/Storage.vue'),
        meta: { title: '存储空间管理', icon: 'DataAnalysis', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/team/efficiency',
        name: 'EfficiencyEvaluation',
        component: () => import('../views/team/EfficiencyEvaluation.vue'),
        meta: { title: '人员效率评价查询', icon: 'DataAnalysis', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/company',
        name: 'Company',
        component: () => import('../views/company/Index.vue'),
        meta: { title: '公司&工作室管理', icon: 'UserFilled', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/video/manage',
        name: 'VideoManage',
        component: () => import('../views/video/VideoManage.vue'),
        meta: { title: '视频管理', icon: 'VideoPlay', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/video/review',
        name: 'VideoReview',
        component: () => import('../views/video/VideoReview.vue'),
        meta: { title: '在线审片', icon: 'View', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/video/storyboard',
        name: 'VideoStoryboard',
        component: () => import('../views/video/VideoStoryboard.vue'),
        meta: { title: '在线分镜', icon: 'Document', permission: 'page:team', requiresAuth: true }
      },
      {
        path: '/video/recording',
        redirect: () => {
          try {
            const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
            const roles = JSON.parse(localStorage.getItem('roles') || '[]')
            const fromCompanies = sessionStorage.getItem('companyAccess:videoDrama') === '1'
            const canDrama =
              roles.includes('admin') ||
              fromCompanies ||
              permissions.includes('company:video:recording:drama')
            if (canDrama) {
              return '/video/recording/drama'
            }
          } catch (e) {
            // ignore
          }
          return '/video/recording/audiobook'
        }
      },
      {
        path: '/video/recording/drama',
        name: 'VideoDramaRecording',
        component: () => import('../views/video/VideoRecording.vue'),
        meta: { title: '视频剧录音', icon: 'Microphone', requiresAuth: true }
      },
      {
        path: '/video/recording/audiobook',
        name: 'AudiobookRecording',
        component: () => import('../views/video/AudiobookRecording.vue'),
        meta: { title: '有声录音', icon: 'Microphone', requiresAuth: true }
      },
      {
        path: '/price',
        name: 'Price',
        component: () => import('../views/price/Index.vue'),
        meta: { title: '制作组价格管理', icon: 'Money', permission: 'page:team' }
      },
      {
        path: '/roles',
        name: 'Roles',
        component: () => import('../views/roles/Index.vue'),
        meta: { title: '角色管理', icon: 'Lock', permission: 'page:roles', requiresAuth: true }
      },
      {
        path: '/account',
        name: 'Account',
        component: () => import('../views/account/Index.vue'),
        meta: { title: '账号管理', icon: 'UserFilled', permission: 'page:account', requiresAuth: true }
      },
      {
        path: '/release',
        name: 'Release',
        component: () => import('../views/release/Index.vue'),
        meta: { title: '软件发布', icon: 'UploadFilled', requiresAuth: true }
      },
      // 音视频处理功能（仅 Electron 环境）
      {
        path: '/media/video-compress',
        name: 'VideoCompress',
        component: () => import('../views/media/VideoCompress.vue'),
        meta: { title: '视频压缩', icon: 'VideoPlay', requiresAuth: true }
      },
      {
        path: '/media/screen-recorder',
        name: 'ScreenRecorder',
        component: () => import('../views/media/ScreenRecorder.vue'),
        meta: { title: '录屏工具', icon: 'VideoCameraFilled', requiresAuth: true }
      },
      {
        path: '/media/audio-replace',
        name: 'AudioReplace',
        component: () => import('../views/media/AudioReplace.vue'),
        meta: { title: '音频替换', icon: 'Switch', requiresAuth: true }
      },
      {
        path: '/media/audio-archive',
        name: 'AudioArchive',
        component: () => import('../views/media/AudioArchive.vue'),
        meta: { title: '音频归档', icon: 'FolderOpened', requiresAuth: true }
      },
      {
        path: '/media/audio-duration-estimate',
        name: 'AudioDurationEstimate',
        component: () => import('../views/media/AudioDurationEstimate.vue'),
        meta: { title: '有效时长估算', icon: 'Timer', requiresAuth: true }
      },
      {
        path: '/media/audio-loudness-normalize',
        name: 'AudioLoudnessNormalize',
        component: () => import('../views/media/AudioLoudnessNormalize.vue'),
        meta: { title: '批量音量均衡', icon: 'Volume', requiresAuth: true }
      }
    ]
  },
  // 分享页面（无需登录，无需Layout）
  {
    path: '/share/voice-showcase',
    name: 'VoiceShowcaseShare',
    component: () => import('../views/share/VoiceShowcaseShare.vue'),
    meta: { title: '声展分享', requiresAuth: false }
  },
  {
    path: '/share/voice-showcase-registry/:token',
    name: 'VoiceShowcaseRegistryShare',
    component: () => import('../views/share/VoiceShowcaseRegistryShare.vue'),
    meta: { title: '声展目录分享', requiresAuth: false }
  },
  {
    path: '/share/storyboard/:token',
    name: 'StoryboardShare',
    component: () => import('../views/share/StoryboardShare.vue'),
    meta: { title: '分镜分享', requiresAuth: false }
  },

  {
    path: '/user-field-test',
    name: 'UserFieldTest',
    component: () => import('../views/test/UserFieldTest.vue'),
    meta: { title: '用户字段测试', requiresAuth: false }
  },
  {
    path: '/qqcallback',
    name: 'QQCallback',
    component: () => import('../views/qqcallback/Index.vue'),
    meta: { title: 'QQ登录回调', requiresAuth: false }
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('../views/error/403.vue'),
    meta: { title: '403 权限不足' }
  },
  {
    // 排除静态资源路径的 catch-all 路由
    // 注意：静态资源路径不应该匹配这个路由，它们应该由 webpack-dev-server 直接提供
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/error/404.vue'),
    meta: { title: '404' },
    beforeEnter: (to, from, next) => {
      // 如果是静态资源路径，不进行路由处理，直接阻止
      const staticExtensions = ['.css', '.js', '.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.mp3', '.mp4', '.woff', '.woff2', '.ttf', '.eot', '.json']
      const staticPaths = ['/css/', '/js/', '/img/', '/favicon']
      
      if (staticExtensions.some(ext => to.path.endsWith(ext)) || staticPaths.some(path => to.path.startsWith(path))) {
        // 静态资源不应该经过路由，直接返回 false 阻止路由导航
        // 这样浏览器会直接请求文件，webpack-dev-server 会提供正确的 MIME 类型
        next(false)
        return
      }
      next()
    }
  }
]

// 在 Electron 环境中使用 hash 模式，避免 file:// 协议下的路由问题
// 在浏览器环境中使用 history 模式
const router = createRouter({
  history: isElectronEnv ? createWebHashHistory() : createWebHistory(),
  routes
})

// 添加路由导航守卫，用于权限检查
router.beforeEach(async (to, from, next) => {
  // 排除静态资源路径，让浏览器直接请求这些文件，不经过路由处理
  // 静态资源应该由 webpack-dev-server 直接提供，不应该经过 Vue Router
  const staticExtensions = ['.css', '.js', '.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.mp3', '.mp4', '.woff', '.woff2', '.ttf', '.eot', '.json']
  const staticPaths = ['/css/', '/js/', '/img/', '/favicon']
  
  // 检查是否是静态资源请求
  const isStaticResource = staticExtensions.some(ext => to.path.endsWith(ext)) || 
                          staticPaths.some(path => to.path.startsWith(path))
  
  if (isStaticResource) {
    // 静态资源不应该经过路由处理
    // 直接阻止导航，让浏览器请求实际文件
    // webpack-dev-server 会直接提供这些文件，不经过 Vue Router
    next(false)
    return
  }
  
  // 检查是否是 Electron 专用路由
  if (to.matched.some(record => record.meta && record.meta.electronOnly)) {
    if (!isElectron()) {
      // 非 Electron 环境，重定向到首页
      console.warn('此功能仅在 Electron 客户端中可用')
      next('/dashboard')
      return
    }
  }
  
  console.log('全局路由日志 to.path:', to.path, 'to.meta:', to.meta, 'to.matched:', to.matched.map(r => r.meta));
  console.log('路由拦截器被触发: ', to.path);
  console.log('to.path:', to.path, 'to.meta:', to.meta);
  console.log('to.matched:', to.matched.map(r => r.meta));
  // 只要任意一级路由 meta.requiresAuth === false 就放行
  if (to.matched.some(record => record.meta && record.meta.requiresAuth === false)) {
    console.log('访问不需要认证的页面:', to.path);
    next();
    return;
  }
  // 获取用户信息
  const token = localStorage.getItem('token')
  console.log('当前token状态:', !!token);
  // 如果没有token，重定向到登录页
  if (!token) {
    console.log('无token，重定向到登录页');
    // Electron hash 模式下，确保使用 hash 路由
    if (isElectronEnv) {
      // 如果当前URL格式错误（pathname=/login 且有 hash），先修正URL
      const pathname = window.location.pathname
      const hash = window.location.hash
      if ((pathname === '/login' || pathname === '/dashboard') && hash && hash.startsWith('#/')) {
        // URL格式错误，修正为正确的 hash 路由格式
        const target = window.location.origin + hash + (window.location.search || '')
        window.history.replaceState(null, '', target)
      }
    }
    next({ path: '/', replace: true });
    return;
  }
  // 公司角色权限控制：仅有对应权限才可访问
  const companyFeaturePermissionMap = [
    { prefix: '/script/online', permission: 'company:script:online' },
    { prefix: '/video/recording/drama', permission: 'company:video:recording:drama' }
  ]
  const matchedCompanyFeature = companyFeaturePermissionMap.find(({ prefix }) => to.path === prefix || to.path.startsWith(prefix + '/'))
  if (matchedCompanyFeature) {
    const roles = JSON.parse(localStorage.getItem('roles') || '[]')
    const isAdminUser = roles.includes('admin')
    let ok = isAdminUser
    if (!ok) {
      const companyAccess = useCompanyAccessStore()
      await companyAccess.ensureLoaded()
      ok = companyAccess.hasCompanyPermission(matchedCompanyFeature.permission)
    }
    if (!ok) {
      console.log(`缺少公司维度权限 ${matchedCompanyFeature.permission}（以 /api/companies 各公司 permissions 为准），重定向到403`)
      next({ path: '/403' })
      return
    }
  }
  
  // 特殊检查：音视频处理功能（仅 Electron 环境，且 isvip 为 1 时可以使用）
  if (to.path.startsWith('/media/')) {
    // 检查是否在 Electron 环境中
    if (!isElectron()) {
      console.warn('音视频处理功能仅在 Electron 客户端中可用')
      next('/dashboard')
      return
    }
    // 检查 isvip 权限
    try {
      // 尝试从 localStorage 获取用户信息
      const userInfoStr = localStorage.getItem('userInfo')
      let isvip = null
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr)
          isvip = userInfo.isvip
        } catch (parseError) {
          console.error('解析 userInfo 失败:', parseError)
        }
      }
      // 如果 isvip !== 1，不允许访问
      if (isvip !== 1) {
        console.log('isvip 不为 1，不允许访问音视频处理功能，重定向到403页面')
        next({ path: '/403' })
        return
      }
      // 如果 isvip 为 null 或 undefined，不允许访问，但会在页面中再次检查
    } catch (error) {
      console.error('检查 isvip 权限失败:', error)
      // 出错时不允许访问，但会在页面中再次检查
      next({ path: '/403' })
      return
    }
  }
  
  // 检查管理员权限
  if (to.meta && to.meta.adminOnly) {
    const roles = JSON.parse(localStorage.getItem('roles') || '[]')
    const isAdmin = roles.includes('admin')
    if (!isAdmin) {
      console.log('非管理员用户，权限不足，重定向到403页面');
      next({ path: '/403' });
      return;
    }
  }
  // 检查权限
  if (to.meta && to.meta.permission) {
    console.log('需要检查权限:', to.meta.permission);
    // 使用统一的权限检查工具
    const hasPermission = canAccessPage(to.meta.permission);
    console.log('是否有权限访问:', hasPermission);
    if (!hasPermission) {
      console.log('权限不足，重定向到403页面');
      next({ path: '/403' });
      return;
    }
  }
  // 有token且有相应权限，允许访问
  next();
})

// Electron 下异步组件加载失败会导致白屏，捕获后打日志便于排查
router.onError((err) => {
  console.error('[Router Error] 路由/组件加载失败（可能白屏）:', err)
})

export default router
